/**
 * Riesgos en la vista previa: los cuatro de la propuesta de ejemplo en el
 * PRJ-001, y el resto de los proyectos vacíos para ver el estado sin riesgos.
 * Crear, editar y borrar cambian la lista en memoria hasta recargar.
 */

import { PROYECTOS_DEMO } from './preview-data';

interface RiesgoSembrado {
  id: number;
  numero: number;
  descripcion: string;
  impacto: 'bajo' | 'medio' | 'alto';
  probabilidad: 'bajo' | 'medio' | 'alto';
  consecuencias: string | null;
  plan_accion: string | null;
  responsable: { id: number | null; nombre: string | null } | null;
  ocurrio: boolean | null;
  ocurrio_el: string | null;
  resultado_mitigacion: string | null;
  eficacia: 'resuelto' | 'mitigado' | 'no_mitigado' | null;
  alta: string | null;
  cargado_por: string | null;
  evaluado_el: string | null;
  evaluado_por: string | null;
  estado: 'identificado' | 'materializado' | 'evaluado' | 'cerrado';
  cerrado_el: string | null;
  cerrado_por: string | null;
}

const YO = 'Silvina Álvarez';

/** La misma regla que `Riesgo::estado()` en el backend */
function estado(r: Pick<RiesgoSembrado, 'ocurrio' | 'eficacia' | 'cerrado_el'>): RiesgoSembrado['estado'] {
  if (r.cerrado_el) return 'cerrado';
  if (r.eficacia) return 'evaluado';
  if (r.ocurrio === true) return 'materializado';
  return 'identificado';
}

/** Completa lo que pone el sistema: fechas, autores y estado */
function sembrado(
  r: Omit<RiesgoSembrado, 'alta' | 'cargado_por' | 'evaluado_el' | 'evaluado_por' | 'estado' | 'cerrado_el' | 'cerrado_por'>,
  extra: { alta: string; evaluado_el?: string; cerrado_el?: string }
): RiesgoSembrado {
  const base = {
    ...r,
    alta: extra.alta,
    cargado_por: 'Matías Salto',
    evaluado_el: extra.evaluado_el ?? null,
    evaluado_por: extra.evaluado_el ? 'Emilce Maidana' : null,
    cerrado_el: extra.cerrado_el ?? null,
    cerrado_por: extra.cerrado_el ? 'Emilce Maidana' : null
  };
  return { ...base, estado: estado(base) };
}

const PERSONAS = [
  { id: 201, nombre: 'Emilce Maidana', email: 'emaidana@vippinn.com' },
  { id: 202, nombre: 'Matías Salto', email: 'msalto@vippinn.com' },
  { id: 203, nombre: 'Silvina Álvarez', email: 'salvarez@vippinn.com' }
];

const persona = (id: number) => {
  const p = PERSONAS.find((x) => x.id === id);
  return p ? { id: p.id, nombre: p.nombre } : null;
};

const porProyecto = new Map<string, RiesgoSembrado[]>([
  ['PRJ-001', [
    sembrado({
      id: 1, numero: 1,
      descripcion: 'Retraso en respuestas de correos y comunicación lenta',
      impacto: 'alto', probabilidad: 'alto',
      consecuencias: 'Demora en el cumplimiento del roadmap\nAcumulación de tickets sin responder',
      plan_accion: 'Reunirnos al menos una vez por semana para evacuar dudas',
      responsable: persona(201),
      ocurrio: true, ocurrio_el: '2026-09-22',
      resultado_mitigacion: 'Se fijó una reunión semanal con el referente y se destrabaron los tickets pendientes.',
      eficacia: 'mitigado'
    }, { alta: '2026-09-01T12:00:00-03:00', evaluado_el: '2026-09-29T10:00:00-03:00' }),
    sembrado({
      id: 2, numero: 2,
      descripcion: 'Sin diseño UX-UI definido',
      impacto: 'alto', probabilidad: 'medio',
      consecuencias: 'Retrabajo de pantallas\nDemora en la aprobación de entregables',
      plan_accion: 'Validar los wireframes con el cliente antes de desarrollar',
      responsable: persona(202),
      ocurrio: true, ocurrio_el: '2026-10-06', resultado_mitigacion: null, eficacia: null
    }, { alta: '2026-09-01T12:00:00-03:00' }),
    sembrado({
      id: 3, numero: 3,
      descripcion: 'Sin accesos al router del cliente',
      impacto: 'medio', probabilidad: 'medio',
      consecuencias: 'No se puede probar el portal cautivo en el sitio',
      plan_accion: 'Pedir los accesos en la reunión de arranque; plan B con un equipo propio',
      responsable: persona(203),
      ocurrio: false, ocurrio_el: null,
      resultado_mitigacion: 'Los accesos llegaron en la primera semana.',
      eficacia: 'resuelto'
    }, { alta: '2026-09-01T12:00:00-03:00', evaluado_el: '2026-09-10T16:00:00-03:00', cerrado_el: '2026-09-12T11:00:00-03:00' }),
    sembrado({
      id: 4, numero: 4,
      descripcion: 'Falta de documentación de la integración existente',
      impacto: 'medio', probabilidad: 'bajo',
      consecuencias: 'Más horas de análisis que las estimadas',
      plan_accion: 'Relevar la integración con el equipo del cliente en la primera semana',
      responsable: null,
      ocurrio: null, ocurrio_el: null, resultado_mitigacion: null, eficacia: null
    }, { alta: '2026-09-15T12:00:00-03:00' })
  ]]
]);

/** El último número usado por proyecto: no se reusa aunque se borre */
const ultimo = new Map<string, number>([['PRJ-001', 4]]);
let siguienteId = 100;

function armar(datos: Record<string, unknown>, id: number, numero: number, antes?: RiesgoSembrado): RiesgoSembrado {
  const ocurrio = (datos['ocurrio'] as boolean | null) ?? null;
  const responsable = datos['responsable_employee_id'] ? persona(Number(datos['responsable_employee_id'])) : null;
  const ahora = new Date().toISOString();
  const r = {
    id,
    numero,
    descripcion: String(datos['descripcion'] ?? ''),
    impacto: datos['impacto'] as RiesgoSembrado['impacto'],
    probabilidad: datos['probabilidad'] as RiesgoSembrado['probabilidad'],
    consecuencias: (datos['consecuencias'] as string | null) ?? null,
    plan_accion: (datos['plan_accion'] as string | null) ?? null,
    responsable,
    ocurrio,
    ocurrio_el: ocurrio === true ? ((datos['ocurrio_el'] as string | null) ?? null) : null,
    resultado_mitigacion: (datos['resultado_mitigacion'] as string | null) ?? null,
    eficacia: (datos['eficacia'] as RiesgoSembrado['eficacia']) ?? null
  };

  /* Como el backend: la fecha de evaluación se mueve solo si cambió algo de
     la evaluación, y la de cierre solo al cerrarlo. */
  // Evaluado es haber juzgado el plan: tener eficacia
  const evaluado = !!r.eficacia;
  const cambio = !antes
    || antes.ocurrio !== r.ocurrio || antes.ocurrio_el !== r.ocurrio_el
    || antes.resultado_mitigacion !== r.resultado_mitigacion || antes.eficacia !== r.eficacia;
  const evaluado_el = !evaluado ? null : cambio ? ahora : antes?.evaluado_el ?? ahora;

  const cerrado_el = datos['cerrado'] ? antes?.cerrado_el ?? ahora : null;

  const completo = {
    ...r,
    alta: antes?.alta ?? ahora,
    cargado_por: antes?.cargado_por ?? YO,
    evaluado_el,
    evaluado_por: !evaluado ? null : cambio ? YO : antes?.evaluado_por ?? YO,
    cerrado_el,
    cerrado_por: cerrado_el ? antes?.cerrado_por ?? YO : null
  };
  return { ...completo, estado: estado(completo) };
}

export function respuestaRiesgos(metodo: string, ruta: string, cuerpo: unknown): unknown | null {
  const m = ruta.match(/\/api\/project\/([^/]+)\/riesgos(?:\/(\d+))?\/?$/);
  if (!m) return null;

  const codigo = decodeURIComponent(m[1]);
  const id = m[2] ? Number(m[2]) : null;
  const lista = porProyecto.get(codigo) ?? [];
  const enCurso = PROYECTOS_DEMO.find((p) => p.code === codigo)?.estado === 'En Curso';
  const datos = (cuerpo ?? {}) as Record<string, unknown>;

  if (metodo === 'GET' && id === null) {
    return {
      success: true,
      editable: true,
      en_curso: enCurso,
      niveles: { bajo: 'Bajo', medio: 'Medio', alto: 'Alto' },
      eficacias: { resuelto: 'Lo resolvió', mitigado: 'Lo mitigó', no_mitigado: 'No alcanzó' },
      estados: { identificado: 'Identificado', materializado: 'Materializado', evaluado: 'Evaluado', cerrado: 'Cerrado' },
      riesgos: lista,
      personas: PERSONAS
    };
  }

  if (metodo === 'POST' && id === null) {
    const numero = (ultimo.get(codigo) ?? 0) + 1;
    ultimo.set(codigo, numero);
    const riesgo = armar(datos, siguienteId++, numero);
    porProyecto.set(codigo, [...lista, riesgo]);
    return { success: true, message: `Riesgo ${numero} agregado.`, riesgo };
  }

  const actual = lista.find((r) => r.id === id);
  if (!actual) {
    return { success: false, message: 'Ese riesgo no es de este proyecto.' };
  }

  if (metodo === 'PUT') {
    const riesgo = armar(datos, actual.id, actual.numero, actual);
    porProyecto.set(codigo, lista.map((r) => (r.id === actual.id ? riesgo : r)));
    return { success: true, message: `Riesgo ${actual.numero} actualizado.`, riesgo };
  }

  if (metodo === 'DELETE') {
    if (lista.length <= 1 && enCurso) {
      return {
        success: false,
        message: 'Es el único riesgo del proyecto y está En Curso: un proyecto en curso tiene que tener al menos un riesgo identificado. Cargá otro antes de borrar este.'
      };
    }
    porProyecto.set(codigo, lista.filter((r) => r.id !== actual.id));
    return { success: true, message: `Riesgo ${actual.numero} eliminado.` };
  }

  return null;
}
